import React, { useEffect, useState } from "react";
import { Hidden } from "@material-ui/core";
import { useRouteMatch } from "react-router-dom";

import Authorities from "../../../../auth/authorities";
import withAuthority from "../../../../components/Auth/withAuthority";
import DefaultAlert from "../../../../components/alerts/DefaultAlert";
import PageHeaderMobile from "../../../../components/common/PageHeader/PageHeaderMobile";
import { ERROR_MESSAGE_UNEXPECTED_ERROR } from "../../../../utils/consts";
import {
  fetchGlobalDepartmentInfo,
  fetchGlobalGroupInfo,
  fetchGlobalProductsInfo,
} from "../../../../services/menuApp/printerMenuService";
import { updateProductInfo } from "../../../../services/menuApp/menuService";
import ThirdPartyPublishNode from "./ThirdPartyPublishNode";

export interface ThirdPartyPublishProps {}

/**
 * ThirdPartyPublish Page
 *
 * Shows the full product list of the location so products can be published to
 * the 3rd party. The list is grouped as group -> department -> product.
 *
 * A card is highlighted when the product's `isDeliverectPublished` is true.
 * Clicking a card sends the product update request with that flag flipped, so
 * a published product becomes unpublished and the other way round. While the
 * request is on its way the card shows a skeleton, and the colour only changes
 * once the response comes back.
 */
const ThirdPartyPublish: React.FunctionComponent<
  ThirdPartyPublishProps
> = () => {
  const [menuNodes, setMenuNodes] = useState<any>([]);
  const [departmentList, setDepartmentList] = useState<any>([]);
  const [selectedCatMenuTitle, setSelectedCatMenuTitle] = useState("");
  const [updatingProductIds, setUpdatingProductIds] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const match: any = useRouteMatch();
  const locationId = (match.params as any).locationId;

  const handleGetMenuInfo = async () => {
    setIsLoading(true);
    try {
      const [groupRes, departmentRes, productRes] = await Promise.all([
        fetchGlobalGroupInfo(locationId),
        fetchGlobalDepartmentInfo(locationId),
        fetchGlobalProductsInfo(locationId),
      ]);

      const nodes: any = handleMenuRestructure(
        groupRes.data.data,
        departmentRes.data.data,
        productRes.data.data,
      );

      setMenuNodes(nodes);
      if (nodes.length > 0) {
        setDepartmentList(nodes[0].department);
        setSelectedCatMenuTitle(nodes[0].name);
      }
    } catch (err) {
      setError(ERROR_MESSAGE_UNEXPECTED_ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Menus - 3rd Party Publish";
    handleGetMenuInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChangeCatMenu = (event: any) => {
    setSelectedCatMenuTitle(event.name);
    const filteredMenu: any = menuNodes.find((d: any) => d.id === event.id);
    if (filteredMenu) {
      setDepartmentList(filteredMenu.department);
    }
  };

  /* Writes the given changes onto one product, in both the full menu tree and
     the department list that is on screen, so the card repaints at once. */
  const applyProductUpdate = (productId: any, changes: any) => {
    const updateProducts = (products: any) =>
      products.map((product: any) =>
        product.id === productId ? { ...product, ...changes } : product,
      );

    setMenuNodes((prev: any) =>
      prev.map((group: any) => ({
        ...group,
        department: group.department.map((department: any) => ({
          ...department,
          product: updateProducts(department.product),
        })),
      })),
    );
    setDepartmentList((prev: any) =>
      prev.map((department: any) => ({
        ...department,
        product: updateProducts(department.product),
      })),
    );
  };

  /* Flips `isDeliverectPublished` on the clicked product and sends the update
     request. The card shows a skeleton until the response comes back - the
     colour is only changed once the update is confirmed. */
  const handleSelectProduct = async (event: any, product: any) => {
    if (event) event.stopPropagation();
    if (!product?.id || updatingProductIds.includes(product.id)) return;

    const isPublished = !!product.isDeliverectPublished;
    const nextIsPublished = !isPublished;

    setUpdatingProductIds((prev: any) => [...prev, product.id]);

    try {
      const productBody: any = {
        id: product.id,
        locationId: locationId,
        version: product.version,
        isDeliverectPublished: nextIsPublished,
      };
      const response = await updateProductInfo(
        locationId,
        productBody,
        "Menus Changed",
        `Product ${product.name} 3rd party publish changed to ${nextIsPublished}`,
        "menu",
      );

      /* The backend can answer with http 200 and still carry an error inside
         the body (for example {status: 404, message: "No product exists with
         given id"}), so the body status is checked as well. */
      const bodyStatus = response?.data?.status;
      const isUpdated =
        (response?.status === 200 || response?.status === 201) &&
        (!bodyStatus || bodyStatus === 200 || bodyStatus === 201);

      if (!isUpdated) {
        // Nothing was changed on screen yet, so the card simply stays as it is.
        setError(response?.data?.message || ERROR_MESSAGE_UNEXPECTED_ERROR);
        return;
      }

      const updatedProduct = response?.data?.data;
      applyProductUpdate(
        product.id,
        updatedProduct || { isDeliverectPublished: nextIsPublished },
      );
      setError("");
    } catch (err) {
      const axiosError: any = err;
      setError(
        axiosError?.response?.data?.message || ERROR_MESSAGE_UNEXPECTED_ERROR,
      );
    } finally {
      setUpdatingProductIds((prev: any) =>
        prev.filter((id: any) => id !== product.id),
      );
    }
  };

  return (
    <>
      <Hidden lgUp>
        <PageHeaderMobile />
      </Hidden>
      <ThirdPartyPublishNode
        isLoading={isLoading}
        menuNodes={menuNodes}
        departmentList={departmentList}
        selectedCatMenuTitle={selectedCatMenuTitle}
        handleChangeCatMenu={handleChangeCatMenu}
        updatingProductIds={updatingProductIds}
        handleSelectProduct={handleSelectProduct}
      />
      <DefaultAlert
        open={!!error}
        handleClose={() => setError("")}
        message={error}
        severity="error"
      />
    </>
  );
};

export default withAuthority(ThirdPartyPublish, Authorities.MENU_READ);

/* Builds group -> department -> product out of the three api responses. */
const handleMenuRestructure = (
  groupNode: any,
  departmentNode: any,
  productNode: any,
) => {
  if (!groupNode || !departmentNode || !productNode) return [];

  const sortedGroups = [...groupNode].sort((a, b) => a.viewOrder - b.viewOrder);
  const sortedDepartments = [...departmentNode].sort(
    (a, b) => a.viewOrder - b.viewOrder,
  );
  const sortedProducts = [...productNode].sort(
    (a, b) => a.viewOrder - b.viewOrder,
  );
  const output: any = [];

  const departmentMap = sortedDepartments.reduce((acc, dep) => {
    acc[dep.groupId] = acc[dep.groupId] || [];
    if (
      dep.id > 0 &&
      !["Online Hidden", "Templates", "Hidden Food", "Hidden Drinks"].includes(
        dep.name,
      )
    ) {
      acc[dep.groupId].push({ ...dep, product: [] });
    }
    return acc;
  }, {});

  const productMap = sortedProducts.reduce((acc, prod) => {
    // The product api returns the department it belongs to as `departmentId`.
    acc[prod.departmentId] = acc[prod.departmentId] || [];
    if (prod.id > 0) {
      acc[prod.departmentId].push(prod);
    }
    return acc;
  }, {});

  sortedGroups.forEach((groupItem) => {
    if (groupItem.id <= 0) return;

    const groupDepartments = departmentMap[groupItem.id] || [];
    groupDepartments.forEach((department: any) => {
      const products = productMap[department.id] || [];
      if (products.length > 0) {
        department.product = products;
      }
    });

    const populatedDepartments = groupDepartments.filter(
      (department: any) => department.product.length > 0,
    );

    if (populatedDepartments.length > 0) {
      output.push({ ...groupItem, department: populatedDepartments });
    }
  });

  return output;
};
